import WidgetModel, { SendDataConfig } from '@mm/state/model/WidgetModel';
import { useViewActions } from '@mm/state/actions/viewActions';
import { StoreState } from '@mm/state/store-interfaces';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import ConnectionModel, {
  ConnectionKind,
} from '@mm/state/model/ConnectionModel';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { selectWidgetGoogleConnections } from '@mm/state/selectors/connectionsSelectors';
import { useGoogleSheetsCRUDActions } from '@mm/state/actions/GoogleSheetsCRUDActions';
import { ReactComponent as GoogleSheetsLogo } from 'assets/google-sheets-logo.svg';
import React, { ReactNode, useEffect } from 'react';
import classNames from 'classnames';
import { Handle, Position, NodeProps } from 'react-flow-renderer';
import { useSelector } from 'react-redux';
import css from './GoogleSheet.module.css';
import common from '../index.module.css';

import { useConnectionWidgetHandlers } from '../../../../../../../../../common/helpers/widget-helpers/use-connection-widget-handlers';
import { Count } from '../elements/Count/Count';
import { useOpenOnReady } from '../use-open-on-ready';
import { uniqueId } from 'lodash';

type Props = { className?: string } & NodeProps<{
  widget: WidgetModel;
}>;

export const GoogleSheet: React.FC<Props> = ({
  className,
  data,
  isDragging,
  children,
}) => {
  const widgets = useSelector((state: StoreState) => state.widget.entities);
  const widget = widgets.find(w => w.id === data.widget.id) || data.widget;
  const config = widget.config as Partial<SendDataConfig>;
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const { connectionWebhookId } = config;
  const connectionWidgetHandlers = useConnectionWidgetHandlers();
  const { create, retrieveWidgetConnections } = useConnectionCRUDActions();
  const { getAccounts } = useGoogleSheetsCRUDActions();
  const viewActions = useViewActions();
  const googleConnections = useSelector(selectWidgetGoogleConnections) ?? [];

  useEffect(() => {
    if (activeFbPageId) {
      retrieveWidgetConnections(activeFbPageId, 'google_spreadsheets');
    }
    getAccounts(activeFbPageId);
  }, [activeFbPageId]);

  const connection = googleConnections.find(c => c.id === connectionWebhookId);

  const onConnectionChange = async (connection: ConnectionModel) => {
    const { sendDataWidgetConfig: config } = connection;
    if (config) {
      const clonedWidget = {
        ...widget,
        config: {
          ...config,
          kind: 'google_sheets' as any,
        },
      };
      await connectionWidgetHandlers.update(clonedWidget);
    }
  };

  const onEditConnection = () => {
    viewActions.showSlideoutMenu('googleSheetIntegrationEditor', {
      widgetId: data.widget.id,
    });
  };

  const onAddNewConnection = async () => {
    const newConnection = await create(activeFbPageId, {
      name: 'Connection',
      kind: ConnectionKind.WIDGET,
      description: '',
      connectionKind: 'google_spreadsheet',
    });
    await retrieveWidgetConnections(activeFbPageId, 'google_spreadsheets');

    if (newConnection) {
      await onConnectionChange(newConnection);

      viewActions.showSlideoutMenu('googleSheetIntegrationEditor', {
        widgetId: data.widget.id,
      });
    }
  };

  const onClick = () => {
    if (isDragging) {
      return;
    }
    if (googleConnections.length) {
      viewActions.showSlideoutMenu('connectionsEditor', {
        widgetId: data.widget.id,
        title: 'Google Sheets Integration',
        selector: selectWidgetGoogleConnections,
        onConnectionChange,
        onAddNewConnection,
        onEditConnection,
        type: 'google_spreadsheets',
      });
    } else {
      viewActions.showSlideoutMenu('googleSheetIntegrationEditor', {
        widgetId: data.widget.id,
      });
    }
  };

  useOpenOnReady(widget, onClick);

  return (
    <>
      <Handle type="target" position={Position.Top} />
      <div
        className={classNames(className, common.card, css.container)}
        onClick={onClick}
      >
        <div className={css.body}>
          <div className={css.header}>
            <div className={css.icon}>
              <GoogleSheetsLogo />
            </div>

            <div>
              {React.Children.map<ReactNode, ReactNode>(children, child => {
                if (React.isValidElement(child)) {
                  return React.cloneElement(child, {
                    // @ts-ignore onEdit does not seemto be present or types are off in @types/react
                    onEdit: onClick,
                    key: uniqueId(),
                  });
                }
              })}
            </div>

            <span className={css.placeholder}>Google Sheets Integration</span>
          </div>
          {connection && (
            <div className={css.info}>
              <span className={css.connectionName}>{connection.name}</span>

              <Count
                className={css.firesCount}
                value={`${data.widget.config?.firesCount || 0} contacts`}
              />
            </div>
          )}
        </div>
      </div>

      <Handle type="source" position={Position.Bottom} />
    </>
  );
};
